import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Trophy, Shield, Crown, Gem, ArrowUpCircle, Info } from 'lucide-react';
import { Button } from '@/components/Button';
import { RadarLevel } from '@/types';

interface LevelInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentLevel: RadarLevel;
    levelExpiresAt: string | null;
    downgradedAt?: string | null;
}

const levelConfig: Record<RadarLevel, { label: string, color: string, icon: React.ReactNode, req: string, mult: string, bonus: string, perks: string[] }> = {
    'bronze': {
        label: 'Bronze',
        color: 'from-amber-700 to-amber-900',
        icon: <Shield className="w-5 h-5 text-amber-700" />,
        req: 'Ao se cadastrar',
        mult: '1.0x',
        bonus: '0',
        perks: [
            'Atendimento padrão'
        ]
    },
    'prata': {
        label: 'Prata',
        color: 'from-slate-300 to-slate-500',
        icon: <Trophy className="w-5 h-5 text-slate-500" />,
        req: '30.000 pts',
        mult: '1.05x',
        bonus: '1.500/mês',
        perks: [
            'Prioridade no atendimento (rápido)',
            '+3% em pontos de compras aprovadas'
        ]
    },
    'ouro': {
        label: 'Ouro',
        color: 'from-yellow-400 to-yellow-600',
        icon: <Crown className="w-5 h-5 text-yellow-600" />,
        req: '100.000 pts',
        mult: '1.10x',
        bonus: '3.000/mês',
        perks: [
            'Prioridade alta no atendimento (+rápido)',
            '+4% em pontos de compras aprovadas'
        ]
    },
    'diamante': {
        label: 'Diamante',
        color: 'from-cyan-300 to-blue-500',
        icon: <Gem className="w-5 h-5 text-blue-500" />,
        req: '250.000 pts',
        mult: '1.20x',
        bonus: '5.000/mês',
        perks: [
            'Prioridade MÁXIMA no atendimento (express)',
            '+4% em pontos de compras aprovadas',
            'Manutenção do Nível: ganhe 50.000 pts a cada 90 dias para manter'
        ]
    }
};

const nextLevelTarget: Record<RadarLevel, number | null> = {
    'bronze': 30000,
    'prata': 100000,
    'ouro': 250000,
    'diamante': null
};

export const LevelInfoModal: React.FC<LevelInfoModalProps> = ({ isOpen, onClose, currentLevel, levelExpiresAt, downgradedAt }) => {
    const [lifetimePoints, setLifetimePoints] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [claimedThisMonth, setClaimedThisMonth] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        const checkPoints = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            // Fetch lifetime positive points
            const { data, error } = await supabase
                .from('points_ledger')
                .select('points')
                .in('entry_type', ['earn_credit', 'adjustment'])
                .gt('points', 0);

            if (!error && data) {
                const total = data.reduce((acc, curr) => acc + (curr.points || 0), 0);
                setLifetimePoints(total);
            }

            // Check if already claimed this month
            const d = new Date();
            const yearMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const { count } = await supabase
                .from('loyalty_user_actions')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', session.user.id)
                .eq('action', 'monthly_level_bonus')
                .eq('period_key', yearMonth);

            if (count && count > 0) {
                setClaimedThisMonth(true);
            }

            setLoading(false);
        };

        checkPoints();
    }, [isOpen]);

    const handleClaimBonus = async () => {
        setClaiming(true);
        setMessage(null);

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Não autenticado');

            const response = await fetch('/.netlify/functions/loyalty-action', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    action: 'monthly_level_bonus'
                })
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || 'Erro ao resgatar bônus');
            }

            setMessage({ text: responseData.message || 'Bônus resgatado com sucesso!', type: 'success' });
            setClaimedThisMonth(true);

        } catch (error: any) {
            setMessage({ text: error.message, type: 'error' });
        } finally {
            setClaiming(false);
        }
    };

    if (!isOpen) return null;

    const targetPoints = nextLevelTarget[currentLevel];
    const progressPercent = targetPoints ? Math.min(100, Math.max(0, (lifetimePoints / targetPoints) * 100)) : 100;

    let isUnderDowngradeCooldown = false;
    let downgradeCooldownEndsAt: Date | null = null;
    if (downgradedAt && currentLevel === 'ouro') {
        downgradeCooldownEndsAt = new Date(new Date(downgradedAt).getTime() + 30 * 24 * 60 * 60 * 1000);
        if (downgradeCooldownEndsAt > new Date()) {
            isUnderDowngradeCooldown = true;
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-800 p-6 md:p-8 animate-in zoom-in-95 duration-200 overflow-y-auto max-h-[90vh]">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="text-center mb-8">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br ${levelConfig[currentLevel].color} shadow-lg shadow-black/10`}>
                        {React.cloneElement(levelConfig[currentLevel].icon as React.ReactElement, { className: 'w-8 h-8 text-white' })}
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-1 uppercase tracking-wider">
                        Nível {levelConfig[currentLevel].label}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                        Aproveitando o melhor do Radar da Economia
                    </p>

                    {currentLevel === 'diamante' && levelExpiresAt && (
                        <div className="mt-4 inline-flex flex-col items-center p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50">
                            <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Manutenção do Nível</span>
                            <span className="text-xs text-blue-800 dark:text-blue-300 font-medium mt-0.5">
                                Válido até {new Date(levelExpiresAt).toLocaleDateString('pt-BR')}
                            </span>
                        </div>
                    )}

                    {isUnderDowngradeCooldown && downgradeCooldownEndsAt && (
                        <div className="mt-6 flex flex-col items-center p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
                            <span className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wider mb-1">Diamante Perdido</span>
                            <span className="text-sm text-red-800 dark:text-red-300 font-medium text-center leading-snug">
                                Poxa... você perdeu seu status Diamante e voltou para o Ouro. <br />
                                Você poderá reconquistá-lo novamente a partir de <strong className="font-extrabold">{downgradeCooldownEndsAt.toLocaleDateString('pt-BR')}</strong>.
                            </span>
                        </div>
                    )}
                </div>

                {!loading && targetPoints && !isUnderDowngradeCooldown && (
                    <div className="mb-8 bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-100 dark:border-gray-800">
                        <div className="flex justify-between text-sm font-bold mb-3">
                            <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1.5 whitespace-nowrap">
                                <ArrowUpCircle className="w-4 h-4 text-emerald-500" />
                                Próximo Nível
                            </span>
                            <span className="text-blue-600 dark:text-blue-400">
                                {lifetimePoints.toLocaleString('pt-BR')} / {targetPoints.toLocaleString('pt-BR')}
                            </span>
                        </div>
                        <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-1000 ease-out relative"
                                style={{ width: `${progressPercent}%` }}
                            >
                                <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]" />
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                            Seu nível depende do total de pontos que você já ganhou.
                            <br />
                            Os pontos gastos <strong>não são</strong> deduzidos do seu total de pontos.
                        </p>
                    </div>
                )}

                {message && (
                    <div className={`mb-6 p-4 rounded-xl text-sm font-medium flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800'}`}>
                        {message.text}
                    </div>
                )}

                <div className="space-y-4">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2 ml-1 text-sm uppercase tracking-wide">Benefícios por Nível</h3>
                    {Object.values(levelConfig).map((lvl) => (
                        <div key={lvl.label} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${currentLevel === lvl.label.toLowerCase() ? `border-transparent bg-gradient-to-br ${lvl.color} text-white shadow-md` : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:border-blue-500/30'}`}>
                            <div className={`p-2 rounded-xl shrink-0 ${currentLevel === lvl.label.toLowerCase() ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
                                {currentLevel === lvl.label.toLowerCase() ? React.cloneElement(lvl.icon as React.ReactElement, { className: 'w-6 h-6 text-white' }) : lvl.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <h4 className={`font-bold text-base ${currentLevel === lvl.label.toLowerCase() ? 'text-white' : ''}`}>{lvl.label}</h4>
                                    <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded-full ${currentLevel === lvl.label.toLowerCase() ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
                                        {lvl.req}
                                    </span>
                                </div>
                                <div className={`flex flex-col sm:flex-row gap-1.5 sm:gap-4 text-xs font-medium mb-3 ${currentLevel === lvl.label.toLowerCase() ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}`}>
                                    <span className="flex items-center gap-1" title="Multiplicador de Pontos por Compras">
                                        Multiplicador de Pontos: <strong className={currentLevel === lvl.label.toLowerCase() ? 'text-white' : 'text-gray-900 dark:text-white'}>{lvl.mult}</strong>
                                    </span>
                                    <span className="flex items-center gap-1" title="Bônus Fixo Mensal (se bater as metas)">
                                        Bônus Mensal de Pontos: <strong className={currentLevel === lvl.label.toLowerCase() ? 'text-white' : 'text-gray-900 dark:text-white'}>{lvl.bonus}</strong>
                                    </span>
                                </div>
                                <div className="space-y-1 mt-1 border-t border-black/5 dark:border-white/5 pt-2">
                                    {lvl.perks.map((perk, idx) => (
                                        <div key={idx} className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                            <div className={`mt-0.5 min-w-[4px] w-1 h-1 rounded-full ${currentLevel === lvl.label.toLowerCase() ? 'bg-white' : 'bg-blue-400'}`}></div>
                                            <span className={currentLevel === lvl.label.toLowerCase() ? 'text-white/90 font-medium' : ''}>{perk}</span>
                                        </div>
                                    ))}
                                </div>
                                {currentLevel === lvl.label.toLowerCase() && lvl.bonus !== '0' && (
                                    <div className="mt-3">
                                        <Button
                                            onClick={handleClaimBonus}
                                            disabled={claimedThisMonth || claiming}
                                            variant={claimedThisMonth ? 'secondary' : 'primary'}
                                            className={`w-full text-xs py-1.5 ${claimedThisMonth ? 'opacity-50 cursor-not-allowed' : 'shadow-lg shadow-white/10 ring-1 ring-white/20'}`}
                                        >
                                            {claiming ? 'Processando...' : claimedThisMonth ? 'Resgatado este mês' : 'Resgatar Mensal'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 flex gap-2 items-start p-4 bg-blue-50 dark:bg-blue-900/10 rounded-2xl text-blue-800 dark:text-blue-300 text-xs leading-relaxed border border-blue-100 dark:border-blue-800/30">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>
                        Os níveis Bronze, Prata e Ouro são vitalícios. Uma vez lá, <strong className="font-bold">você nunca mais cai</strong> de nível. Apenas o nível Diamante exige uma manutenção a cada 90 dias com acúmulo de pontos, para provar quem são os verdadeiros monstros dos descontos!
                    </p>
                </div>

                <div className="mt-6">
                    <Button onClick={onClose} variant="secondary" className="w-full">
                        Entendi
                    </Button>
                </div>
            </div>
        </div>
    );
};
